import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid2 as Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { FulfillmentAssignmentRule } from 'api/models/FulfillmentAssignmentRule';
import { SlimUser } from 'api/models/SlimUser';
import { UserProfile } from 'api/models/UserProfile';
import { ApiService } from 'api/services/ApiService';
import { userSearchQueryKey, userSearchStaleTime } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { AttioConsultantService } from 'dashboard409a/services/attio/AttioConsultantService';
import { AttioEngagementService } from 'dashboard409a/services/attio/AttioEngagementService';
import { AttioTeamData } from 'dashboard409a/services/attio/AttioTeamService';
import { useDisplaySnack } from 'dashboard409a/utilities';
import {
  ASSIGNMENT_CONDITION_FIELD_OPTIONS,
  ASSIGNMENT_CONDITION_OPERATORS,
} from './assignmentRuleConditions.constants';
import StaffPreparerPicker from './StaffPreparerPicker';

type AssignmentRulePreviewResponse = {
  matched_rule_ids: number[];
  would_add_preparer_ids: number[];
  final_preparer_ids: number[];
};

type ConditionRow = {
  id: string;
  field: string;
  operator: string;
  value: string;
};

type RuleTrigger = NonNullable<FulfillmentAssignmentRule['trigger']>;

const TRIGGER_ON_CLIENT_DOCS_RECEIVED = 'on_client_docs_received' as RuleTrigger;
const TRIGGER_ON_CLIENT_DOCS_ASSIGN_INTERN = 'on_client_docs_assign_intern' as RuleTrigger;

const newRowId = () => `cond-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const defaultConditionRows = (): ConditionRow[] => [
  {
    id: newRowId(),
    field: ASSIGNMENT_CONDITION_FIELD_OPTIONS[0]?.value ?? 'status',
    operator: 'equals',
    value: '',
  },
];

const buildConditionPayload = (rows: ConditionRow[]): Record<string, unknown> => {
  const all = rows.map(row => {
    const { field, operator, value } = row;
    if (operator === 'is_empty' || operator === 'is_not_empty') {
      return { field, operator };
    }
    if (operator === 'in' || operator === 'not_in') {
      const parts = value.split(',').map(s => s.trim()).filter(Boolean);
      return { field, operator, value: parts };
    }
    return { field, operator, value };
  });
  return { all };
};

const PERSON_SEARCHABLE_FIELDS = new Set([
  'valuedInformation.valuationLeadName',
  'valuedInformation.valuationEngagementManager',
]);

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase();

const FulfillmentAssignmentRules = () => {
  const displaySnack = useDisplaySnack();
  const displaySnackRef = useRef(displaySnack);
  const [rules, setRules] = useState<FulfillmentAssignmentRule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<FulfillmentAssignmentRule | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [trigger, setTrigger] = useState<RuleTrigger>(TRIGGER_ON_CLIENT_DOCS_RECEIVED);
  const [conditionRows, setConditionRows] = useState<ConditionRow[]>(defaultConditionRows);
  const [selectedPreparers, setSelectedPreparers] = useState<UserProfile[]>([]);
  const [previewValuationText, setPreviewValuationText] = useState(
    '{\n  "valuedInformation": {\n    "valuationLeadName": "John Doe"\n  }\n}'
  );
  const [previewTrigger, setPreviewTrigger] = useState<RuleTrigger>(TRIGGER_ON_CLIENT_DOCS_RECEIVED);
  const [previewExistingPreparersText, setPreviewExistingPreparersText] = useState('');
  const [previewResult, setPreviewResult] = useState<AssignmentRulePreviewResponse | null>(null);
  const [personSearchResultsByRow, setPersonSearchResultsByRow] = useState<Record<string, AttioTeamData[]>>({});
  const [personSearchLoadingByRow, setPersonSearchLoadingByRow] = useState<Record<string, boolean>>({});
  const personSearchTimersRef = useRef<Record<string, ReturnType<typeof setTimeout> | undefined>>({});

  const consultantService = useMemo(() => new AttioConsultantService(), []);
  const engagementService = useMemo(() => new AttioEngagementService(), []);

  // Staff lookup — same query key as StaffPreparerPicker, so it's served from cache
  const { data: staffList = [] } = useQuery({
    queryKey: [userSearchQueryKey, 'initial-staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList(undefined);
      return (response as SlimUser[]) || [];
    },
    staleTime: userSearchStaleTime,
  });

  const staffById = useMemo<Map<number, string>>(
    () =>
      new Map(
        staffList
          .filter((user): user is SlimUser & { id: number } => user.id != null)
          .map(user => [
            user.id,
            user.first_name
              ? `${user.first_name} ${user.last_name ?? ''}`.trim()
              : user.email ?? String(user.id),
          ])
      ),
    [staffList]
  );

  const triggerOptions = useMemo(
    () => [
      { value: TRIGGER_ON_CLIENT_DOCS_RECEIVED, label: 'Client Docs Received' },
      { value: TRIGGER_ON_CLIENT_DOCS_ASSIGN_INTERN, label: 'Client Docs Received (Assign Intern)' },
    ],
    []
  );

  const getTriggerLabel = useCallback(
    (value: string) => triggerOptions.find(o => o.value === value)?.label ?? value,
    [triggerOptions]
  );

  const renderConditionChips = useCallback((conditions: unknown) => {
    try {
      const parsed = conditions as { all?: Array<{ field: string; operator: string; value?: string }> };
      const rows = parsed?.all ?? [];
      if (!rows.length) {
        return <Typography variant="caption" color="text.secondary">No conditions</Typography>;
      }
      return (
        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {rows.map((cond, i) => {
            const fieldLabel =
              ASSIGNMENT_CONDITION_FIELD_OPTIONS.find(o => o.value === cond.field)?.label ?? cond.field;
            const operatorLabel =
              ASSIGNMENT_CONDITION_OPERATORS.find(o => o.value === cond.operator)?.label ?? cond.operator;
            const label =
              cond.value !== undefined
                ? `${fieldLabel} ${operatorLabel} "${cond.value}"`
                : `${fieldLabel} ${operatorLabel}`;
            return <Chip key={i} size="small" label={label} variant="outlined" />;
          })}
        </Stack>
      );
    } catch {
      return <Typography variant="caption" color="text.secondary">{String(conditions)}</Typography>;
    }
  }, []);

  const renderPreparerAvatars = useCallback(
    (preparerIds: number[] = []) => {
      if (!preparerIds.length) {
        return (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            No preparers assigned
          </Typography>
        );
      }
      const MAX_VISIBLE = 4;
      const visible = preparerIds.slice(0, MAX_VISIBLE);
      const overflow = preparerIds.length - MAX_VISIBLE;
      return (
        <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
          {visible.map(id => {
            const displayName = staffById.get(id) ?? `ID ${id}`;
            return (
              <Chip
                key={id}
                size="small"
                avatar={<Avatar>{getInitials(displayName)}</Avatar>}
                label={displayName}
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            );
          })}
          {overflow > 0 && (
            <Typography variant="caption" color="text.secondary">
              +{overflow} more
            </Typography>
          )}
        </Stack>
      );
    },
    [staffById]
  );

  const parseIdList = (raw: string): number[] =>
    raw
      .split(',')
      .map(value => Number(value.trim()))
      .filter(value => Number.isInteger(value) && value > 0);

  useEffect(() => {
    displaySnackRef.current = displaySnack;
  }, [displaySnack]);

  const loadRules = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await ApiService.apiDashboardFulfillmentAssignmentRulesList();
      setRules(response);
    } catch {
      displaySnackRef.current('Failed to load assignment rules', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const updateConditionRow = useCallback((id: string, patch: Partial<ConditionRow>) => {
    setConditionRows(prev => prev.map(row => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const addConditionRow = useCallback(() => {
    setConditionRows(prev => [
      ...prev,
      {
        id: newRowId(),
        field: ASSIGNMENT_CONDITION_FIELD_OPTIONS[0]?.value ?? 'status',
        operator: 'equals',
        value: '',
      },
    ]);
  }, []);

  const removeConditionRow = useCallback((id: string) => {
    setConditionRows(prev => (prev.length <= 1 ? prev : prev.filter(row => row.id !== id)));
  }, []);

  const handlePersonSearchInputChange = useCallback(
    (row: ConditionRow, inputValue: string) => {
      if (!PERSON_SEARCHABLE_FIELDS.has(row.field)) {
        return;
      }

      const existingTimer = personSearchTimersRef.current[row.id];
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      if (!inputValue.trim()) {
        setPersonSearchResultsByRow(prev => ({ ...prev, [row.id]: [] }));
        setPersonSearchLoadingByRow(prev => ({ ...prev, [row.id]: false }));
        return;
      }

      personSearchTimersRef.current[row.id] = setTimeout(() => {
        setPersonSearchLoadingByRow(prev => ({ ...prev, [row.id]: true }));
        const run = async () => {
          try {
            if (row.field === 'valuedInformation.valuationEngagementManager') {
              const managers = await engagementService.searchEngagementManagers(inputValue);
              setPersonSearchResultsByRow(prev => ({
                ...prev,
                [row.id]: managers.map(m => ({ targetRecordId: m.targetRecordId, name: m.name, imageSrc: m.imageSrc })),
              }));
            } else {
              const consultants = await consultantService.searchConsultants(inputValue);
              setPersonSearchResultsByRow(prev => ({
                ...prev,
                [row.id]: consultants.map(c => ({
                  targetRecordId: (c as unknown as { targetRecordId?: string }).targetRecordId || c.name,
                  name: c.name,
                  imageSrc: c.imageSrc,
                })),
              }));
            }
          } finally {
            setPersonSearchLoadingByRow(prev => ({ ...prev, [row.id]: false }));
          }
        };
        run().catch(() => {
          setPersonSearchResultsByRow(prev => ({ ...prev, [row.id]: [] }));
          setPersonSearchLoadingByRow(prev => ({ ...prev, [row.id]: false }));
        });
      }, 350);
    },
    [consultantService, engagementService]
  );

  const handleCreate = useCallback(async () => {
    const conditionsPayload = buildConditionPayload(conditionRows);
    const preparerIds = selectedPreparers.map(u => u.id).filter((id): id is number => typeof id === 'number');

    const payload: FulfillmentAssignmentRule = {
      name: name.trim(),
      description: description.trim(),
      trigger,
      conditions: conditionsPayload,
      preparers: preparerIds,
      is_active: true,
    };

    if (!payload.name) {
      displaySnack('Rule name is required', 'error');
      return;
    }
    if (!preparerIds.length) {
      displaySnack('Select at least one preparer', 'error');
      return;
    }

    setIsSaving(true);
    try {
      await ApiService.apiDashboardFulfillmentAssignmentRulesCreate(payload);
      displaySnack('Assignment rule created', 'success');
      setName('');
      setDescription('');
      setSelectedPreparers([]);
      setConditionRows(defaultConditionRows());
      await loadRules();
    } catch {
      displaySnack('Failed to create assignment rule', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [conditionRows, description, displaySnack, loadRules, name, selectedPreparers, trigger]);

  const handleToggleActive = useCallback(
    async (rule: FulfillmentAssignmentRule) => {
      if (!rule.id) {
        return;
      }
      try {
        await ApiService.apiDashboardFulfillmentAssignmentRulesPartialUpdate(String(rule.id), {
          is_active: !rule.is_active,
        } as FulfillmentAssignmentRule);
        await loadRules();
      } catch {
        displaySnack('Failed to update rule status', 'error');
      }
    },
    [displaySnack, loadRules]
  );

  const handleDeleteConfirmed = useCallback(async () => {
    if (!ruleToDelete?.id) {
      return;
    }
    try {
      await ApiService.apiDashboardFulfillmentAssignmentRulesDelete(String(ruleToDelete.id));
      displaySnack('Assignment rule deleted', 'success');
      await loadRules();
    } catch {
      displaySnack('Failed to delete assignment rule', 'error');
    } finally {
      setRuleToDelete(null);
    }
  }, [displaySnack, loadRules, ruleToDelete]);

  const handlePreview = useCallback(async () => {
    let valuationPayload: unknown;
    try {
      valuationPayload = JSON.parse(previewValuationText);
    } catch {
      displaySnack('Preview valuation payload must be valid JSON', 'error');
      return;
    }

    setIsPreviewing(true);
    try {
      const result = await ApiService.apiDashboardFulfillmentAssignmentRulesPreview({
        trigger: previewTrigger,
        valuation: valuationPayload,
        existing_preparer_ids: parseIdList(previewExistingPreparersText),
      } as unknown as FulfillmentAssignmentRule);
      setPreviewResult(result as unknown as AssignmentRulePreviewResponse);
    } catch {
      displaySnack('Preview failed', 'error');
    } finally {
      setIsPreviewing(false);
    }
  }, [displaySnack, previewExistingPreparersText, previewTrigger, previewValuationText]);

  const activeCount = useMemo(() => rules.filter(r => r.is_active).length, [rules]);

  const VALUE_REQUIRED_OPERATORS = useMemo(
    () => new Set(['equals', 'not_equals', 'in', 'not_in']),
    []
  );

  const isFormValid = useMemo(() => {
    if (!name.trim()) {
      return false;
    }
    if (!selectedPreparers.length) {
      return false;
    }
    const allConditionsValid = conditionRows.every(row => {
      if (VALUE_REQUIRED_OPERATORS.has(row.operator)) {
        return row.value.trim().length > 0;
      }
      return true;
    });
    return allConditionsValid;
  }, [name, selectedPreparers.length, conditionRows, VALUE_REQUIRED_OPERATORS]);

  return (
    <>
      <Stack spacing={3}>
        {/* ── Create Rule ─────────────────────────────────────────── */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Create Assignment Rule
          </Typography>
          <Divider sx={{ mb: 2.5 }} />

          <Stack spacing={2.5}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 7 }}>
                <TextField
                  fullWidth
                  label="Name"
                  value={name}
                  onChange={event => setName(event.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  fullWidth
                  select
                  label="Trigger"
                  value={trigger}
                  onChange={event => setTrigger(event.target.value as RuleTrigger)}
                >
                  {triggerOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <TextField
              label="Description"
              value={description}
              onChange={event => setDescription(event.target.value)}
              multiline
              minRows={2}
            />

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Conditions
                </Typography>
                <Chip
                  size="small"
                  label={`${conditionRows.length} — all must match`}
                  sx={{ fontSize: '0.7rem' }}
                />
              </Stack>
              <Stack spacing={1.5}>
                {conditionRows.map(row => {
                  const isPersonSearchField = PERSON_SEARCHABLE_FIELDS.has(row.field);
                  return (
                    <Stack
                      key={row.id}
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      alignItems={{ md: 'center' }}
                    >
                      <TextField
                        select
                        label="Field"
                        value={row.field}
                        onChange={e => updateConditionRow(row.id, { field: e.target.value })}
                        sx={{ minWidth: { md: 220 }, '& .MuiSelect-select:focus': { backgroundColor: 'transparent !important' } }}
                      >
                        {ASSIGNMENT_CONDITION_FIELD_OPTIONS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Operator"
                        value={row.operator}
                        onChange={e => updateConditionRow(row.id, { operator: e.target.value })}
                        sx={{ minWidth: { md: 160 }, '& .MuiSelect-select:focus': { backgroundColor: 'transparent !important' } }}
                      >
                        {ASSIGNMENT_CONDITION_OPERATORS.map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      {isPersonSearchField ? (
                        <Autocomplete
                          freeSolo
                          options={personSearchResultsByRow[row.id] || []}
                          getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
                          isOptionEqualToValue={(option, value) => option.targetRecordId === value.targetRecordId}
                          loading={Boolean(personSearchLoadingByRow[row.id])}
                          value={null}
                          inputValue={row.value}
                          onChange={(_, selected) => {
                            if (typeof selected === 'string') {
                              updateConditionRow(row.id, { value: selected });
                            } else {
                              updateConditionRow(row.id, { value: selected?.name || '' });
                            }
                          }}
                          onInputChange={(_, inputValue, reason) => {
                            if (reason === 'input') {
                              updateConditionRow(row.id, { value: inputValue });
                            }
                            handlePersonSearchInputChange(row, inputValue);
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label="Value"
                              sx={{ flex: 1, minWidth: { md: 220 }, '& .MuiSelect-select:focus': { backgroundColor: 'transparent !important' } }}
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {personSearchLoadingByRow[row.id] ? (
                                        <CircularProgress color="inherit" size={16} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                },
                              }}
                            />
                          )}
                        />
                      ) : (
                        <TextField
                          label="Value"
                          value={row.value}
                          onChange={e => updateConditionRow(row.id, { value: e.target.value })}
                          sx={{ flex: 1, '& .MuiSelect-select:focus': { backgroundColor: 'transparent !important' } }}
                        />
                      )}
                      <Tooltip title="Remove condition">
                        <span>
                          <IconButton
                            aria-label="Remove condition"
                            onClick={() => removeConditionRow(row.id)}
                            disabled={conditionRows.length <= 1}
                            color="error"
                            size="small"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  );
                })}
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={addConditionRow}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add condition
                </Button>
              </Stack>
            </Box>

            <StaffPreparerPicker
              selectedPreparers={selectedPreparers}
              onSelectedPreparersChange={setSelectedPreparers}
              title="Preparers to assign when this rule matches"
            />

            <Box>
              <Button variant="contained" onClick={handleCreate} loading={isSaving} disabled={!isFormValid}>
                Create Rule
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* ── Existing Rules ──────────────────────────────────────── */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
            <Typography variant="h6" fontWeight={600}>
              Existing Rules
            </Typography>
            {!isLoading && rules.length > 0 && (
              <Chip
                size="small"
                label={`${activeCount} active / ${rules.length} total`}
                color={activeCount > 0 ? 'success' : 'default'}
                variant="outlined"
              />
            )}
          </Stack>
          <Divider sx={{ mb: 2 }} />

          {(() => {
            if (isLoading) {
              return (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Loading rules…</Typography>
                </Stack>
              );
            }

            if (rules.length === 0) {
              return (
                <Stack alignItems="center" spacing={1.5} sx={{ py: 5, color: 'text.disabled' }}>
                  <AssignmentLateOutlinedIcon sx={{ fontSize: 40 }} />
                  <Typography variant="body2">No assignment rules yet.</Typography>
                  <Typography variant="caption" color="text.disabled">
                    Create a rule above to automatically assign preparers to new fulfillments.
                  </Typography>
                </Stack>
              );
            }

            return (
              <Stack spacing={1.5}>
                {rules.map(rule => (
                  <Paper key={rule.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.25}>
                      {/* Header row */}
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {rule.name}
                          </Typography>
                          {rule.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                              {rule.description}
                            </Typography>
                          )}
                        </Box>

                        <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                          <Tooltip title={rule.is_active ? 'Deactivate rule' : 'Activate rule'}>
                            <Switch
                              size="small"
                              checked={Boolean(rule.is_active)}
                              onChange={() => handleToggleActive(rule)}
                              color="success"
                            />
                          </Tooltip>
                          <Typography
                            variant="caption"
                            color={rule.is_active ? 'success.main' : 'text.disabled'}
                            sx={{ minWidth: 48 }}
                          >
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </Typography>
                          <Tooltip title="Delete rule">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setRuleToDelete(rule)}
                              aria-label="Delete rule"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>

                      {/* Trigger + conditions */}
                      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
                        <Chip
                          icon={<BoltIcon />}
                          size="small"
                          label={getTriggerLabel(rule.trigger ?? '')}
                          color="primary"
                          variant="outlined"
                        />
                        {renderConditionChips(rule.conditions)}
                      </Stack>

                      {/* Preparers */}
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Preparers
                        </Typography>
                        {renderPreparerAvatars(rule.preparers)}
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            );
          })()}
        </Paper>

        {/* ── Preview Match (collapsed by default) ────────────────── */}
        <Accordion disableGutters elevation={1} sx={{ borderRadius: 1, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight={600}>
              Preview Match
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                select
                label="Preview Trigger"
                value={previewTrigger}
                onChange={event => setPreviewTrigger(event.target.value as RuleTrigger)}
              >
                {triggerOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Valuation Payload JSON"
                value={previewValuationText}
                onChange={event => setPreviewValuationText(event.target.value)}
                multiline
                minRows={8}
              />
              <TextField
                label="Existing Preparer IDs (comma-separated)"
                value={previewExistingPreparersText}
                onChange={event => setPreviewExistingPreparersText(event.target.value)}
                helperText="Simulate preparers already on the fulfillment before automation merge."
              />
              <Box>
                <Button variant="outlined" onClick={handlePreview} loading={isPreviewing}>
                  Run Preview
                </Button>
              </Box>
              {previewResult && (
                <TextField
                  label="Preview Result"
                  value={JSON.stringify(previewResult, null, 2)}
                  multiline
                  minRows={6}
                  slotProps={{ input: { readOnly: true } }}
                />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>

      {/* ── Delete confirmation dialog ───────────────────────────── */}
      <Dialog
        open={Boolean(ruleToDelete)}
        onClose={() => setRuleToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete rule?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{ruleToDelete?.name}</strong> will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRuleToDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FulfillmentAssignmentRules;
